import { useAuth } from "@clerk/nextjs";
import { getAuth } from "@clerk/nextjs/server";
import type { GetServerSideProps } from "next";
import { useRouter } from "next/compat/router";
import { NextSeo } from "next-seo";
import { useEffect } from "react";
import { mutate } from "swr";

import CertificationModule from "../components/CertificationModule";
import CertificationOutOfScopeOverlay from "../components/CertificationOutOfScopeOverlay";
import CertificationSidebar from "../components/CertificationSidebar";
import { useCertification } from "~/hooks";
import { GridLayout } from "~/layouts";
import type { NextPageWithProps } from "~/types";
import fetcher from "~/utils/fetcher.client";

const CertificationModulePage: NextPageWithProps = () => {
	const { certification } = useCertification();

	const router = useRouter();

	const { userId } = useAuth();

	useEffect(() => {
		if (certification.isNotAllowed && certification.isLegacy) {
			router?.replace(
				`/certifications/${certification.slug.replace("-original", "")}`,
			);
		}
	}, [
		certification.isLegacy,
		certification.isNotAllowed,
		certification.slug,
		router,
	]);

	useEffect(() => {
		if (userId) {
			mutate(`/certifications`);
			mutate(`/certifications/${router?.query.certificationSlug}`);
			mutate(
				`/certifications/${router?.query.certificationSlug}/modules/${router?.query.moduleSlug}`,
			);
		}
	}, [router?.query.certificationSlug, router?.query.moduleSlug, userId]);

	return (
		<>
			<NextSeo
				title={certification.title}
				description={certification.description}
				openGraph={{
					images: [
						{
							url: certification.thumbnail.url,
							alt: certification.thumbnail.alt,
						},
					],
				}}
			/>

			<CertificationOutOfScopeOverlay />

			<GridLayout.Sidebar className="hidden xl:block">
				<CertificationSidebar />
			</GridLayout.Sidebar>

			<GridLayout.Body>
				<CertificationModule />
			</GridLayout.Body>
		</>
	);
};

CertificationModulePage.Layout = GridLayout;

export const getServerSideProps: GetServerSideProps = async ({
	params,
	req,
}) => {
	try {
		const { getToken } = getAuth(req);
		const token = (await getToken()) ?? undefined;

		const [certification, module, certifications, recommendedCoursePlan] =
			await Promise.all([
				fetcher(`/certifications/${params!.certificationSlug}`, {
					token,
				}),
				fetcher(
					`/certifications/${params!.certificationSlug}/modules/${
						params!.moduleSlug
					}`,
					{ token },
				),
				fetcher(`/certifications`, { token }),
				fetcher(
					`/certifications/${params!.certificationSlug}/recommended-course-plan`,
					{ token },
				),
			]);

		const breadcrumbs = [
			{
				name: certification.title,
				href: `/certifications/${certification.slug}`,
			},

			{
				name: "Modules",
				href: `/certifications/${certification.slug}`,
			},

			module
				? {
						name: module.title,
						href: `/certifications/${certification.slug}/modules/${module.slug}`,
					}
				: undefined,
		].filter(Boolean) as {
			name: string;
			href: string;
		}[];

		return {
			props: {
				breadcrumbs,
				fallback: {
					[`/certifications/${params!.certificationSlug}`]: certification,
					[`/certifications/${params!.certificationSlug}/modules/${
						params!.moduleSlug
					}`]: module,
					[`/certifications`]: certifications,
					[`/certifications/${
						params!.certificationSlug
					}/recommended-course-plan`]: recommendedCoursePlan,
				},
			},
		};
	} catch (_error) {
		return {
			notFound: true,
		};
	}
};

export default CertificationModulePage;
